import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import {Route,BrowserRouter,Routes,HashRouter,matchPath,useLocation,useNavigate} from 'react-router-dom'
import { needsPayment, getSignupDraft } from '../utils/signupDraft'
import { readBlock } from '../utils/blockNotice'
import Header from '../comps/header'
import CreateUploadBar from '../comps/createUploadBar'
// pages
import Home from '../screens/home'
import Notifications from '../screens/notifications'
import Search from '../screens/search'
import Watch from '../screens/watch'
import SoundPage from '../screens/sound'
import Settings from '../screens/settings'
import ShotVideo from '../screens/shotVideo'
import Auth from '../screens/Auth'
import AuthPassword from '../screens/authPassword'
import AuthVerify from '../screens/authVerify'
import AuthOauth from '../screens/authOauth'
import AuthName from '../screens/authName'
import AuthPhoto from '../screens/authPhoto'
import AuthBio from '../screens/authBio'
import Visted from '../screens/visted'
import Vist from '../screens/vist'
import VistedLead from '../screens/vist2'
import SetVideo from '../screens/setVideo'
import PostPage from '../screens/post'
// import SetVideoo from '../screens/setVideoo'
import Chat from '../screens/chat'
import Error404 from '../screens/error404'
import MapPagee from '../screens/mapPage'
import Person from '../screens/person'
import ContactUs from '../screens/contactUs'
import AboutUs from '../screens/aboutUs'
import Policy from '../screens/policy'
import Payment from '../screens/payment'
import Studio from '../screens/studio'
import Earnings from '../screens/earnings'
import Admin from '../screens/admin'
import Career from '../screens/career'
import Ben from '../screens/ben'
import Main from '../screens/mian'
import Rooms from '../screens/rooms'
import Room from '../screens/room'
import Communities from '../screens/communities'
import Community from '../screens/community'
import Zombie from '../screens/zombie'
import Video from '../comps/video'
import Image from '../comps/image'
import { FiX } from 'react-icons/fi'
import { useSelector } from 'react-redux'
import '../styles/miniPlayer.css'
import { MiniPlayerContext, useMiniPlayer } from '../store/miniPlayerContext'
import { mediaPath } from '../utils/mediaRoute'

const APP_PATHS = [
  '/',
  '/profile',
  '/notifications',
  '/search',
  '/rooms',
  '/rooms/:id',
  '/community',
  '/community/:id',
  '/zombie',
  '/watch',
  '/watch/:category',
  '/sound/:soundId',
  '/settings',
  '/shot-video',
  '/@:id',
  '/following',
  '/followers',
  '/video/:id',
  '/post/:id',
  '/chat/:id',
  '/maps',
  '/maps/',
  '/person',
  '/person/',
  '/contact-us',
  '/about-us',
  '/policy',
  '/career',
  '/payment',
  '/studio',
  '/earnings',
  '/admin',
  '/ben',
  '/auth',
  '/auth/oauth',
  '/auth/setup/code',
  '/auth/setup/password',
  '/auth/setup/name',
  '/auth/setup/bio',
  '/auth/setup/photo',
]

const HIDE_NAV_PREFIXES = ['/auth', '/chat/', '/payment', '/video/', '/post/', '/admin', '/ben', '/zombie']

const isAppPath = (pathname) =>
  APP_PATHS.some((path) => matchPath({ path, end: true }, pathname))

const Navbar = () => {
  const {pathname} = useLocation();

  if (!isAppPath(pathname)) return null;
  if (HIDE_NAV_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return null;
  if (/^\/rooms\/.+/.test(pathname)) return null;

  return <Header />;
}

const MiniPlayer = () => {
  const { miniVideo, closeMiniPlayer, miniFromRectRef, playbackRef, expanding, setExpanding } = useMiniPlayer();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [geometry, setGeometry] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const [pending, setPending] = useState(true);
  const [morphing, setMorphing] = useState(false);
  const boxRef = useRef(null);
  const interaction = useRef(null);
  const resumeStamp = useRef(null);
  const CHROME = { pad: 0, title: 0 };

  const fitBox = (left, top, width) => {
    const minW = 200;
    const maxW = Math.max(minW, window.innerWidth - left - 8);
    const w = Math.min(Math.max(minW, width), maxW);
    const videoH = Math.max(112, (w - CHROME.pad) * 9 / 16);
    let h = videoH + CHROME.title + CHROME.pad;
    const maxH = Math.max(160, window.innerHeight - top - 8);
    if (h > maxH) {
      const nextVideoH = Math.max(112, maxH - CHROME.title - CHROME.pad);
      const nextW = Math.min(maxW, Math.max(minW, nextVideoH * 16 / 9 + CHROME.pad));
      return { left, top, width: nextW, height: nextVideoH + CHROME.title + CHROME.pad };
    }
    return { left, top, width: w, height: h };
  };

  useEffect(() => {
    const move = event => {
      const current = interaction.current;
      if (!current) return;
      event.preventDefault();
      const dx = event.clientX - current.x;
      const dy = event.clientY - current.y;

      if (current.mode === 'drag') {
        const width = current.width;
        const height = current.height;
        setGeometry({
          left: Math.max(8, Math.min(window.innerWidth - width - 8, current.left + dx)),
          top: Math.max(8, Math.min(window.innerHeight - height - 8, current.top + dy)),
          width,
          height
        });
        return;
      }
      const grow = Math.abs(dx) >= Math.abs(dy) ? dx : dy * (16 / 9);
      setGeometry(fitBox(current.left, current.top, current.width + grow));
    };
    const stop = event => {
      if (interaction.current && event && event.pointerId != null) {
        try { event.currentTarget && event.currentTarget.releasePointerCapture(event.pointerId); } catch (_) {}
      }
      interaction.current = null;
    };
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
  }, []);

  const beginInteraction = (mode, event) => {
    event.preventDefault();
    event.stopPropagation();
    const node = boxRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch (_) {}
    interaction.current = {
      mode,
      x: event.clientX,
      y: event.clientY,
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height
    };
    setGeometry({ left: rect.left, top: rect.top, width: rect.width, height: rect.height });
  };

  const hidden = !miniVideo || (!expanding && (pathname.startsWith('/video/') || pathname.startsWith('/post/') || pathname.startsWith('/auth') || pathname.startsWith('/watch') || pathname.startsWith('/payment') || pathname.startsWith('/rooms')));
  const wasHidden = useRef(true);

  const reportTime = (info) => {
    if (!playbackRef || !miniVideo) return;
    playbackRef.current = {
      id: miniVideo.id,
      time: info.time || 0,
      playing: !!info.playing
    };
  };

  const expandToVideo = () => {
    if (!miniVideo || expanding) return;
    const node = boxRef.current;
    if (node) {
      const rect = node.getBoundingClientRect();
      setGeometry({ left: rect.left, top: rect.top, width: rect.width, height: rect.height });
    }
    setExpanding(true);
    navigate(mediaPath(miniVideo), { state: { item: miniVideo } });
  };

  useLayoutEffect(() => {
    if (!expanding) return undefined;
    let cancelled = false;
    let tries = 0;
    const follow = () => {
      if (cancelled) return;
      const dest = document.querySelector('.SetVideoPage__playerWrapper');
      const box = dest && dest.getBoundingClientRect();
      if (!box || box.width < 40 || box.height < 40) {
        if (tries++ < 45) window.requestAnimationFrame(follow);
        else setExpanding(false);
        return;
      }
      setGeometry({ left: box.left, top: box.top, width: box.width, height: box.height });
      window.setTimeout(() => {
        if (cancelled) return;
        setExpanding(false);
        setGeometry(null);
      }, 520);
    };
    const frame = window.requestAnimationFrame(follow);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
    };
  }, [expanding, pathname, setExpanding]);

  useLayoutEffect(() => {
    if (expanding) return undefined;
    if (hidden) {
      wasHidden.current = true;
      setPending(true);
      setLeaving(false);
      setMorphing(false);
      if (!pathname.startsWith('/video/') && !pathname.startsWith('/post/') && miniFromRectRef) {
        miniFromRectRef.current = null;
      }
      return undefined;
    }
    if (!wasHidden.current) return undefined;
    wasHidden.current = false;
    const node = boxRef.current;
    if (!node) return undefined;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const from = miniFromRectRef && miniFromRectRef.current;
    const videoNode = node.querySelector('.miniPlayer__video');

    if (!reduceMotion && from && from.width > 40 && videoNode) {
      const dest = videoNode.getBoundingClientRect();
      const dx = from.left - dest.left;
      const dy = from.top - dest.top;
      const scale = Math.max(from.width / Math.max(dest.width, 1), from.height / Math.max(dest.height, 1));
      node.classList.add('is-morphing');
      videoNode.style.transition = 'none';
      videoNode.style.transformOrigin = 'top left';
      videoNode.style.willChange = 'transform';
      videoNode.style.borderRadius = '0px';
      videoNode.style.transform = `translate3d(${dx}px, ${dy}px, 0) scale(${scale})`;
      setMorphing(true);
      setPending(false);
      let cancelled = false;
      const frame = window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (cancelled || !videoNode.isConnected) return;
          void videoNode.offsetWidth;
          videoNode.style.transition = 'transform 0.52s cubic-bezier(0.22, 1, 0.36, 1), border-radius 0.45s ease';
          videoNode.style.transform = 'translate3d(0, 0, 0) scale(1)';
          videoNode.style.borderRadius = '12px';
        });
      });
      const done = window.setTimeout(() => {
        if (cancelled) return;
        videoNode.style.transition = '';
        videoNode.style.transform = '';
        videoNode.style.transformOrigin = '';
        videoNode.style.willChange = '';
        videoNode.style.borderRadius = '';
        node.classList.remove('is-morphing');
        setMorphing(false);
        if (miniFromRectRef) miniFromRectRef.current = null;
      }, 540);
      return () => {
        cancelled = true;
        window.cancelAnimationFrame(frame);
        window.clearTimeout(done);
      };
    }

    if (miniFromRectRef) miniFromRectRef.current = null;
    setPending(false);
    if (reduceMotion) return undefined;
    node.classList.add('is-appear');
    const done = window.setTimeout(() => node.classList.remove('is-appear'), 480);
    return () => window.clearTimeout(done);
  }, [hidden, miniVideo, miniFromRectRef, pathname, expanding]);

  const hideMini = (event) => {
    event?.stopPropagation?.();
    setLeaving(true);
    window.setTimeout(() => {
      setLeaving(false);
      setGeometry(null);
      closeMiniPlayer();
    }, 280);
  };

  if (!miniVideo || (hidden && !leaving && !expanding)) return null;
  if (!resumeStamp.current || resumeStamp.current.id !== miniVideo.id) {
    resumeStamp.current = {
      id: miniVideo.id,
      time: playbackRef && playbackRef.current && playbackRef.current.id === miniVideo.id
        ? playbackRef.current.time
        : 0
    };
  }

  return (
    <aside
      ref={boxRef}
      className={`miniPlayer${pending ? ' is-pending' : ''}${leaving ? ' is-leave' : ''}${morphing ? ' is-morphing' : ''}${expanding ? ' is-expanding' : ''}`}
      aria-label="Now playing"
      style={geometry ? {
        left: geometry.left,
        top: geometry.top,
        width: geometry.width,
        height: geometry.height,
        right: 'auto',
        bottom: 'auto'
      } : undefined}
    >
      <button className="miniPlayer__dragHandle" type="button" onPointerDown={event => beginInteraction('drag', event)} aria-label="Move player">
        <span className="miniPlayer__grip" />
      </button>
      <button className="miniPlayer__close" type="button" onClick={hideMini} aria-label="Close video"><FiX /></button>
      <div className="miniPlayer__video" onClick={expandToVideo}>
        {miniVideo.type === 'i'
          ? <Image title={miniVideo.title} image={miniVideo.image} report={null} share={null} />
          : <Video
              title={miniVideo.title}
              data={`/api/uploads/${miniVideo.image}`}
              report={null}
              share={null}
              compact
              disableFullscreen
              startAt={resumeStamp.current.time}
              autoPlay
              onProgress={reportTime}
              onOpen={expandToVideo}
            />}
      </div>
      <div className="miniPlayer__bar">
        <button className="miniPlayer__title" type="button" onClick={expandToVideo}>
          <strong>{miniVideo.title}</strong>
          {miniVideo.email ? <span>{miniVideo.email}</span> : null}
        </button>
      </div>
      <button className="miniPlayer__resizeHandle" type="button" onPointerDown={event => beginInteraction('resize', event)} aria-label="Resize player" />
    </aside>
  );
};

const BlockedGate = () => {
  const { pathname } = useLocation()
  const [block, setBlock] = useState(() => readBlock())

  useEffect(() => {
    const sync = () => setBlock(readBlock())
    window.addEventListener('pinweelo-block', sync)
    window.addEventListener('storage', sync)
    const tick = window.setInterval(sync, 4000)
    return () => {
      window.removeEventListener('pinweelo-block', sync)
      window.removeEventListener('storage', sync)
      window.clearInterval(tick)
    }
  }, [])

  if (!block || pathname.startsWith('/admin') || pathname.startsWith('/notifications')) return null
  return (
    <div className="blockWall">
      <h1>You are blocked</h1>
      <p>{block.text}</p>
    </div>
  )
}

const PaymentGate = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!needsPayment()) return;
    if (pathname.startsWith('/payment')) return;
    if (pathname.startsWith('/admin')) return;
    if (!localStorage.getItem('userdata')) return;
    if (pathname.startsWith('/auth') && getSignupDraft().email) return;
    navigate('/payment', { replace: true });
  }, [pathname, navigate]);

  return null;
};

const needsAccountPath = pathname =>
  pathname === '/shot-video' || pathname === '/ben' || pathname === '/notifications' || pathname.startsWith('/notifications/')

const AuthRequiredGate = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const userId = useSelector(state => state.authReduse && state.authReduse.id);

  useEffect(() => {
    if (!needsAccountPath(pathname)) return;
    if (userId) return;
    try {
      const saved = JSON.parse(localStorage.getItem('userdata') || 'null');
      if (saved && saved.id) return;
    } catch (err) {}
    navigate('/auth', { replace: true, state: { from: pathname } });
  }, [pathname, navigate, userId]);

  return null;
};

const Navigation = () =>{
  const [miniVideo, setMiniVideo] = useState(null);
  const [expanding, setExpanding] = useState(false);
  const miniFromRectRef = useRef(null);
  const playbackRef = useRef({ id: null, time: 0, playing: true });
  const closeMiniPlayer = useCallback(() => {
    setExpanding(false);
    setMiniVideo(null);
  }, []);
  const setMiniFromRect = useCallback((rect) => {
    miniFromRectRef.current = rect;
  }, []);
  const setMiniVideoSafe = useCallback((video) => {
    setMiniVideo((prev) => {
      const nextId = video && video.id;
      if (nextId && playbackRef.current.id !== nextId) {
        playbackRef.current = { id: nextId, time: 0, playing: true };
      }
      return video;
    });
  }, []);

  return(
 <MiniPlayerContext.Provider value={{miniVideo, setMiniVideo: setMiniVideoSafe, closeMiniPlayer, miniFromRectRef, setMiniFromRect, playbackRef, expanding, setExpanding}}>
 <BrowserRouter>
{/* <HashRouter> */}
 <PaymentGate />
 <BlockedGate />
 <AuthRequiredGate />
 <Navbar />
 <CreateUploadBar />

 <Routes>
   <Route path='/auth' element={<Auth />} />
   <Route path='/auth/oauth' element={<AuthOauth />} />
   <Route path='/auth/setup/code' element={<AuthVerify />} />
   <Route path='/auth/setup/password' element={<AuthPassword />} />
   <Route path='/auth/setup/name' element={<AuthName />} />
   <Route path='/auth/setup/bio' element={<AuthBio />} />
   <Route path='/auth/setup/photo' element={<AuthPhoto />} />


  <Route path='/' exact element={<Main />} />

<Route path='/profile' element={<Home />} />
<Route path='/notifications' element={<Notifications />} />
<Route path='/search' element={<Search />} />
<Route path='/rooms' element={<Rooms />} />
<Route path='/rooms/:id' element={<Room />} />
<Route path='/community' element={<Communities />} />
<Route path='/community/:id' element={<Community />} />
<Route path='/zombie' element={<Zombie />} />
<Route path='/watch' element={<Watch />} />
<Route path='/watch/:category' element={<Watch />} />
<Route path='/sound/:soundId' element={<SoundPage />} />
<Route path='/settings' element={<Settings />} />
<Route path='/shot-video' element={<ShotVideo />} />
<Route path='/@:id' element={<Visted />} />
<Route path='/following' element={<Vist />} />
<Route path='/followers' element={<VistedLead />} />
<Route path='/video/:id' element={<SetVideo />} />
<Route path='/post/:id' element={<PostPage />} />
{/* <Route path='/video/:vieotitle' element={<SetVideoo />} /> */}
<Route path='/chat/:id' element={<Chat />} />
<Route path='/maps/' element={<MapPagee />} />
<Route path='/person/' element={<Person />} />

  <Route path='/contact-us' element={<ContactUs />} />
  <Route path='/about-us' element={<AboutUs />} />
  <Route path='/policy' element={<Policy />} />
  <Route path='/career' element={<Career />} />
  <Route path='/payment' element={<Payment />} />
  <Route path='/studio' element={<Studio />} />
  <Route path='/earnings' element={<Earnings />} />
  <Route path='/admin' element={<Admin />} />
<Route path='/ben' element={<Ben />} />


<Route path="*" element={<Error404/>} />

 </Routes>
 <MiniPlayer />
 {/* </HashRouter> */}
  </BrowserRouter>
 </MiniPlayerContext.Provider>



)
}
export default Navigation;
